import { ArrowLeftOutlined, SearchOutlined } from '@ant-design/icons'
import { Col, Input, Modal, Row } from 'antd'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from '../../contexts/Auth'
import { MasterVehicleBrand, MasterVehicleModel } from '../../services/master/vehicle.interfaces'
import { getVehicleBrands, getVehicleModels } from '../../services/master/vehicles.services'
import { uploadPhotoFile } from '../../services/upload/upload.service'
import CarWithDriverCarTypeModal from './CarWithDriverCarTypeModal'
import { inferCarType } from './CarWithDriverPricing'
import { BottomNav, RequiredMark, TopBar } from './CarWithDriverShared'
import { useCwdLang } from './useCwdLang'

const modalStyles = `
  .cwd-brand-modal .ant-modal-content { border-radius: 16px !important; }
  .cwd-brand-modal .ant-modal-header { border-radius: 16px 16px 0 0 !important; padding: 16px 20px !important; border-bottom: 1px solid #f0f0f0 !important; }
  .cwd-brand-modal .ant-modal-title { font-family: 'Poppins', sans-serif !important; font-weight: 600 !important; }
  .cwd-brand-modal .ant-modal-body { padding: 12px 20px 20px !important; overflow-y: auto !important; max-height: 70vh !important; }

  .cwd-model-modal .ant-modal-content { border-radius: 16px !important; }
  .cwd-model-modal .ant-modal-header { border-radius: 16px 16px 0 0 !important; padding: 16px 20px !important; border-bottom: 1px solid #f0f0f0 !important; }
  .cwd-model-modal .ant-modal-title { font-family: 'Poppins', sans-serif !important; font-weight: 600 !important; }
  .cwd-model-modal .ant-modal-body { padding: 12px 20px 20px !important; overflow-y: auto !important; max-height: 70vh !important; }

  .cwd-search-input .ant-input-affix-wrapper { height: 48px !important; border-radius: 8px !important; border-color: #D6DAE0 !important; padding: 0 16px !important; align-items: center !important; }
  .cwd-search-input .ant-input-affix-wrapper .ant-input { height: 46px !important; line-height: 46px !important; padding: 0 !important; border: none !important; box-shadow: none !important; font-family: 'Poppins', sans-serif !important; }
  .cwd-search-input .ant-input-affix-wrapper:focus-within { border-color: #0048B3 !important; box-shadow: 0 0 0 2px rgba(0,72,179,0.2) !important; }
  .cwd-search-input .ant-input-prefix { margin-right: 12px !important; color: #999 !important; }

  .cwd-brand-grid-item { display: flex !important; flex-direction: column !important; align-items: center !important; padding: 16px 12px !important; border: 1px solid #f0f0f0 !important; border-radius: 8px !important; cursor: pointer !important; background: white !important; height: 100px !important; justify-content: center !important; margin-bottom: 8px !important; }
  .cwd-brand-grid-item.selected { border-color: #0048B3 !important; background-color: #f6f9ff !important; box-shadow: 0 4px 12px rgba(0,72,179,0.2) !important; }
  .cwd-brand-logo { width: 40px !important; height: 40px !important; object-fit: contain !important; margin-bottom: 6px !important; }
  .cwd-brand-name { font-size: 12px !important; font-weight: 400 !important; color: #212427 !important; text-align: center !important; line-height: 1.2 !important; overflow: hidden !important; display: -webkit-box !important; -webkit-line-clamp: 2 !important; -webkit-box-orient: vertical !important; font-family: 'Poppins', sans-serif !important; }
  .cwd-brand-grid-item.selected .cwd-brand-name { color: #0048B3 !important; }

  .cwd-model-pill { display: flex !important; align-items: center !important; justify-content: center !important; border-radius: 8px !important; cursor: pointer !important; background: #f8f9fa !important; border: 1px solid #e9ecef !important; margin-bottom: 8px !important; width: 100% !important; min-height: 48px !important; text-align: center !important; padding: 8px 4px !important; }
  .cwd-model-pill.selected { background: #0048B3 !important; border-color: #0048B3 !important; box-shadow: 0 2px 8px rgba(0,72,179,0.3) !important; }
  .cwd-model-pill-text { font-size: 12px !important; font-weight: 400 !important; color: #212427 !important; line-height: 1.2 !important; overflow: hidden !important; display: -webkit-box !important; -webkit-line-clamp: 2 !important; -webkit-box-orient: vertical !important; font-family: 'Poppins', sans-serif !important; }
  .cwd-model-pill.selected .cwd-model-pill-text { color: white !important; }

  .cwd-model-modal-title { display: flex !important; align-items: center !important; gap: 8px !important; }
  .cwd-model-modal-back { display: flex !important; align-items: center !important; cursor: pointer !important; color: #212427 !important; font-size: 16px !important; }

  .cwd-no-data { text-align: center !important; padding: 40px 20px !important; color: #999 !important; font-size: 15px !important; font-family: 'Poppins', sans-serif !important; }
`

export interface CarEntry {
  id: string
  carType: string
  carBrand: string
  model: string
  quantity: number
  maxPassengers: number
  maxStandardLuggages: number
  maxSmallLuggages: number
  images: {
    trunkSpace?: string
    frontView?: string
    sideView?: string
    backView?: string
  }
}

interface CarWithDriverAddCarFormProps {
  entry?: CarEntry
  onConfirm: (entry: CarEntry) => void
  onBack: () => void
  onExit: () => void
}

const IMAGE_SLOTS = [
  {
    key: 'trunkSpace',
    icon: 'https://ts-upload.sgp1.digitaloceanspaces.com/cms/2NYv9A6b6CJqVUYBfmgmahuujTNP7vFGYa6s99vpioVvHkRy5PZXw1ScoJGLUpoWxrvhSJqWbJ1uibZszjkv3qgb.png',
  },
  {
    key: 'frontView',
    icon: 'https://ts-upload.sgp1.digitaloceanspaces.com/cms/5p7hHNJpYLDw9b3cGViqkP6PoDiCxXcPgj1khwcWwL4cQKYkRycDmnXcwT31YoLjgPcs6ofY2J5R84R9bkRr1bzE.png',
  },
  {
    key: 'sideView',
    icon: 'https://ts-upload.sgp1.digitaloceanspaces.com/cms/26snSZeWDJ751d9mghKsmx9r7tr8tHH7BxgJP4Z9kpXVS98aLRPzin7YzaxKbCy5sborSvfYHZJMP2oWJ4giKbpy.png',
  },
  {
    key: 'backView',
    icon: 'https://ts-upload.sgp1.digitaloceanspaces.com/cms/4X1cvsqGsP9HLX3rFrctMAe1tgiMYqf8j9uvhrPkNppuKjw4rMsPkjFqyn4SasxyiTkwuzuQJPX35aTw16jaegMS.png',
  },
] as const

interface ModelGroup {
  key: string
  label: string
  items: MasterVehicleModel[]
}

const formatModelKey = (key: string) => key.replace(/_/g, ' ').trim()

const Stepper: React.FC<{
  value: number
  min?: number
  max?: number
  onChange: (v: number) => void
}> = ({ value, min = 1, max = 99, onChange }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '0' }}>
    <button
      onClick={() => onChange(Math.max(min, value - 1))}
      style={{
        width: '32px',
        height: '32px',
        borderRadius: '50%',
        border: '1px solid #D6DAE0',
        backgroundColor: '#fff',
        cursor: 'pointer',
        fontSize: '18px',
        lineHeight: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#333',
      }}
    >
      −
    </button>
    <span
      style={{
        minWidth: '32px',
        textAlign: 'center',
        fontSize: '16px',
        fontWeight: '500',
        color: '#333',
      }}
    >
      {value}
    </span>
    <button
      onClick={() => onChange(Math.min(max, value + 1))}
      style={{
        width: '32px',
        height: '32px',
        borderRadius: '50%',
        border: '1px solid #D6DAE0',
        backgroundColor: '#fff',
        cursor: 'pointer',
        fontSize: '18px',
        lineHeight: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#333',
      }}
    >
      +
    </button>
  </div>
)

const CarWithDriverAddCarForm: React.FC<CarWithDriverAddCarFormProps> = ({
  entry,
  onConfirm,
  onBack,
  onExit,
}) => {
  const { userLoggedIn } = useAuth()
  const { lang, t } = useCwdLang()

  const [carType, setCarType] = useState(entry?.carType ?? '')
  const [carBrand, setCarBrand] = useState(entry?.carBrand ?? '')
  const [model, setModel] = useState(entry?.model ?? '')
  const [quantity, setQuantity] = useState(entry?.quantity ?? 1)
  const [maxPassengers, setMaxPassengers] = useState(entry?.maxPassengers ?? 1)
  const [maxStandardLuggages, setMaxStandardLuggages] = useState(entry?.maxStandardLuggages ?? 1)
  const [maxSmallLuggages, setMaxSmallLuggages] = useState(entry?.maxSmallLuggages ?? 1)
  const [images, setImages] = useState<CarEntry['images']>(entry?.images ?? {})
  const [showCarTypeModal, setShowCarTypeModal] = useState(false)
  const [uploading, setUploading] = useState<Record<string, boolean>>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [brands, setBrands] = useState<MasterVehicleBrand[]>([])
  const [models, setModels] = useState<MasterVehicleModel[]>([])
  // true while carType is the value inferred from the selected model
  const [carTypeAuto, setCarTypeAuto] = useState(false)
  const [brandsLoading, setBrandsLoading] = useState(false)
  const [modelsLoading, setModelsLoading] = useState(false)
  const [showBrandModal, setShowBrandModal] = useState(false)
  const [showModelModal, setShowModelModal] = useState(false)
  const [brandSearch, setBrandSearch] = useState('')
  const [modelSearch, setModelSearch] = useState('')
  // Model modal is two-step like the normal submission: model group → sub-model
  const [modelStep, setModelStep] = useState<'model' | 'subModel'>('model')
  const [activeModelKey, setActiveModelKey] = useState<string | null>(null)

  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({})

  useEffect(() => {
    setBrandsLoading(true)
    getVehicleBrands()
      .then((data) => setBrands(Array.isArray(data) ? data : []))
      .catch(() => setBrands([]))
      .finally(() => setBrandsLoading(false))
  }, [])

  useEffect(() => {
    if (!carBrand) {
      setModels([])
      return
    }
    let cancelled = false
    setModelsLoading(true)
    getVehicleModels(carBrand)
      .then((data) => {
        if (cancelled) return
        const list = (Array.isArray(data) ? data : []).filter(
          (m) => (m.vehicletype || '').toUpperCase() !== 'MOTO',
        )
        const seen = new Set<string>()
        setModels(
          list.filter((m) => {
            const id = `${m.key}|${m.sub_model}|${m.description_th}`
            return !seen.has(id) && !!seen.add(id)
          }),
        )
      })
      .catch(() => {
        if (!cancelled) setModels([])
      })
      .finally(() => {
        if (!cancelled) setModelsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [carBrand])

  const brandName = (b: MasterVehicleBrand) =>
    (lang === 'EN' && b.description_en?.trim()) || b.description_th
  const modelName = (m: MasterVehicleModel) =>
    (lang === 'EN' && m.description_en?.trim()) || m.description_th

  const filteredBrands = useMemo(() => {
    const list = [...brands].sort((a, b) => brandName(a).localeCompare(brandName(b), 'th'))
    if (!brandSearch) return list
    const q = brandSearch.toLowerCase()
    return list.filter(
      (b) =>
        b.description_th.toLowerCase().includes(q) ||
        (b.description_en || '').toLowerCase().includes(q) ||
        b.key.toLowerCase().includes(q),
    )
  }, [brands, brandSearch, lang])

  // "CAMRY_Premium" → "Premium" (strip the brand/model prefix from sub_model).
  // '' means the row is the plain model itself, not a sub-model.
  const cleanedSubModel = (m: MasterVehicleModel) => {
    const raw = m.sub_model || ''
    const stripped = [`${carBrand}_${m.key}`, m.key].reduce((current, prefix) => {
      const re = new RegExp(`^${prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}_?`, 'i')
      return re.test(current) ? current.replace(re, '') : current
    }, raw)
    return stripped.replace(/_/g, ' ').trim()
  }

  const subModelLabel = (m: MasterVehicleModel) => cleanedSubModel(m) || modelName(m)

  const modelGroups = useMemo(() => {
    const groups = new Map<string, MasterVehicleModel[]>()
    models.forEach((m) => groups.set(m.key, [...(groups.get(m.key) ?? []), m]))
    return Array.from(groups.entries())
      .map(([key, items]): ModelGroup => ({ key, label: formatModelKey(key), items }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [models])

  const filteredModelGroups = useMemo(() => {
    if (!modelSearch) return modelGroups
    const q = modelSearch.toLowerCase()
    return modelGroups.filter(
      (g) =>
        g.label.toLowerCase().includes(q) ||
        g.items.some(
          (m) =>
            m.description_th.toLowerCase().includes(q) ||
            (m.description_en || '').toLowerCase().includes(q),
        ),
    )
  }, [modelGroups, modelSearch])

  const activeGroup = modelGroups.find((g) => g.key === activeModelKey) ?? null

  // Same as the normal submission: hide the plain-model row (no sub-model) so the
  // model name isn't repeated in the sub-model list, unless it's the only option.
  const visibleSubModels = useMemo(() => {
    if (!activeGroup) return []
    const genuine = activeGroup.items.filter((m) => cleanedSubModel(m) !== '')
    return genuine.length > 0 ? genuine : activeGroup.items
  }, [activeGroup])

  const selectedBrand = brands.find((b) => b.key === carBrand)
  const selectedModel = models.find((m) => m.description_th === model)

  const handleModelSelect = (m: MasterVehicleModel) => {
    const inferred = inferCarType(m)
    setModel(m.description_th)
    setCarType(inferred)
    setCarTypeAuto(!!inferred)
    setErrors((prev) => ({ ...prev, model: '', carType: '' }))
  }

  const closeModelModal = () => {
    setShowModelModal(false)
    setModelStep('model')
    setActiveModelKey(null)
    setModelSearch('')
  }

  const handleModelGroupSelect = (group: ModelGroup) => {
    if (group.items.length === 1) {
      handleModelSelect(group.items[0])
      closeModelModal()
      return
    }
    setActiveModelKey(group.key)
    setModelStep('subModel')
  }

  const handleCarTypeSelect = (type: string) => {
    setCarType(type)
    setCarTypeAuto(false)
    setErrors((prev) => ({ ...prev, carType: '' }))
  }

  const handleUpload = async (slotKey: string, file: File) => {
    setUploading((prev) => ({ ...prev, [slotKey]: true }))
    try {
      const fileUrl = await uploadPhotoFile(file, userLoggedIn?.userToken)
      setImages((prev) => ({ ...prev, [slotKey]: fileUrl }))
    } catch (err: any) {
      alert(err?.message || err || t.addCar.uploadFailed)
    } finally {
      setUploading((prev) => ({ ...prev, [slotKey]: false }))
    }
  }

  const hasAtLeastOneImage = Object.values(images).some((v) => !!v)

  const validate = () => {
    const e: Record<string, string> = {}
    if (!carBrand) e.carBrand = t.addCar.errBrand
    if (!model.trim()) e.model = t.addCar.errModel
    if (!carType) e.carType = t.addCar.errCarType
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const handleConfirm = () => {
    if (!validate()) return
    onConfirm({
      id: entry?.id ?? String(Date.now()),
      carType,
      carBrand,
      model,
      quantity,
      maxPassengers,
      maxStandardLuggages,
      maxSmallLuggages,
      images,
    })
  }

  return (
    <div
      style={{
        fontFamily: 'Poppins, sans-serif',
        backgroundColor: '#fff',
        minHeight: '100vh',
        maxWidth: '500px',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
      }}
    >
      <TopBar onExit={onExit} />

      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 16px' }}>
        <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#212427', margin: '0 0 20px' }}>
          {t.addCar.title}
        </h2>

        {/* Car brand */}
        <label
          style={{
            fontSize: '14px',
            fontWeight: '500',
            color: '#333',
            display: 'block',
            marginBottom: '6px',
          }}
        >
          {t.addCar.brand}
          <RequiredMark />
        </label>
        <div
          onClick={() => !brandsLoading && setShowBrandModal(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            border: `1px solid ${errors.carBrand ? '#ef4444' : '#D6DAE0'}`,
            borderRadius: '8px',
            cursor: brandsLoading ? 'default' : 'pointer',
            marginBottom: errors.carBrand ? '4px' : '16px',
            backgroundColor: brandsLoading ? '#F4F5F9' : '#fff',
          }}
        >
          <span style={{ fontSize: '14px', color: selectedBrand ? '#333' : '#9CA3AF' }}>
            {brandsLoading
              ? t.common.loading
              : selectedBrand
              ? brandName(selectedBrand)
              : t.addCar.selectBrand}
          </span>
          <span style={{ color: '#667085', fontSize: '12px' }}>▼</span>
        </div>
        {errors.carBrand && (
          <p style={{ color: '#ef4444', fontSize: '12px', margin: '0 0 12px' }}>
            {errors.carBrand}
          </p>
        )}

        {/* Model */}
        <label
          style={{
            fontSize: '14px',
            fontWeight: '500',
            color: '#333',
            display: 'block',
            marginBottom: '6px',
          }}
        >
          {t.addCar.model}
          <RequiredMark />
        </label>
        <div
          onClick={() => carBrand && !modelsLoading && setShowModelModal(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            border: `1px solid ${errors.model ? '#ef4444' : '#D6DAE0'}`,
            borderRadius: '8px',
            cursor: carBrand && !modelsLoading ? 'pointer' : 'default',
            marginBottom: errors.model ? '4px' : '16px',
            backgroundColor: !carBrand || modelsLoading ? '#F4F5F9' : '#fff',
          }}
        >
          <span style={{ fontSize: '14px', color: model ? '#333' : '#9CA3AF' }}>
            {modelsLoading
              ? t.common.loading
              : !carBrand
              ? t.addCar.selectBrandFirst
              : (selectedModel ? modelName(selectedModel) : model) || t.addCar.selectModel}
          </span>
          <span style={{ color: '#667085', fontSize: '12px' }}>▼</span>
        </div>
        {errors.model && (
          <p style={{ color: '#ef4444', fontSize: '12px', margin: '0 0 12px' }}>{errors.model}</p>
        )}

        {/* Car type — inferred from the selected model, can be changed */}
        <label
          style={{
            fontSize: '14px',
            fontWeight: '500',
            color: '#333',
            display: 'block',
            marginBottom: '6px',
          }}
        >
          {t.common.carType}
          <RequiredMark />
        </label>
        <div
          onClick={() => model && setShowCarTypeModal(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            border: `1px solid ${errors.carType ? '#ef4444' : '#D6DAE0'}`,
            borderRadius: '8px',
            cursor: model ? 'pointer' : 'default',
            marginBottom: '4px',
            backgroundColor: model ? '#fff' : '#F4F5F9',
          }}
        >
          <span style={{ fontSize: '14px', color: carType ? '#333' : '#9CA3AF' }}>
            {carType || (model ? t.common.selectCarType : t.addCar.selectModelFirst)}
          </span>
          <span style={{ color: '#667085', fontSize: '12px' }}>▼</span>
        </div>
        {errors.carType ? (
          <p style={{ color: '#ef4444', fontSize: '12px', margin: '0 0 12px' }}>{errors.carType}</p>
        ) : (
          model && (
            <p style={{ color: '#667085', fontSize: '12px', margin: '0 0 16px' }}>
              {carTypeAuto ? t.addCar.carTypeAutoHint : !carType && t.addCar.carTypeManualHint}
            </p>
          )
        )}
        {!model && !errors.carType && <div style={{ height: '12px' }} />}

        {/* Info note */}
        <div
          style={{
            backgroundColor: '#EFF6FF',
            borderRadius: '8px',
            padding: '12px',
            marginBottom: '20px',
            display: 'flex',
            gap: '8px',
          }}
        >
          <span style={{ color: '#0048B3', fontSize: '14px', flexShrink: 0 }}>ⓘ</span>
          <p style={{ margin: 0, fontSize: '12px', color: '#667085', lineHeight: '1.5' }}>
            {t.addCar.infoNote}
          </p>
        </div>

        {/* Quantity */}
        {[
          {
            label: t.addCar.quantity,
            sub: t.addCar.quantitySub,
            value: quantity,
            set: setQuantity,
            min: 1,
          },
          {
            label: t.addCar.maxPassengers,
            sub: undefined,
            value: maxPassengers,
            set: setMaxPassengers,
            min: 1,
          },
          {
            label: t.addCar.maxStandardLuggage,
            sub: t.addCar.luggageStandardSize,
            value: maxStandardLuggages,
            set: setMaxStandardLuggages,
            min: 0,
          },
          {
            label: t.addCar.maxSmallLuggage,
            sub: t.addCar.luggageSmallSize,
            value: maxSmallLuggages,
            set: setMaxSmallLuggages,
            min: 0,
          },
        ].map(({ label, sub, value, set, min }) => (
          <div
            key={label}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px',
            }}
          >
            <div>
              <p style={{ margin: 0, fontSize: '14px', fontWeight: '500', color: '#333' }}>
                {label}
              </p>
              {sub && <p style={{ margin: 0, fontSize: '12px', color: '#667085' }}>{sub}</p>}
            </div>
            <Stepper value={value} min={min} onChange={set} />
          </div>
        ))}

        {/* Car images */}
        <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#333', margin: '8px 0 16px' }}>
          {t.addCar.carImages}
          <RequiredMark />
        </h3>
        {IMAGE_SLOTS.map(({ key, icon }) => {
          const label = t.addCar.imageSlots[key]
          return (
            <div key={key} style={{ marginBottom: '16px' }}>
              <p style={{ margin: '0 0 8px', fontSize: '14px', fontWeight: '500', color: '#333' }}>
                {label}
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                {/* Uploaded image or placeholder */}
                <div
                  style={{
                    height: '100px',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    border: '1px solid #D6DAE0',
                    backgroundColor: '#F4F5F9',
                  }}
                >
                  {images[key as keyof typeof images] ? (
                    <img
                      src={images[key as keyof typeof images]}
                      alt={label}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <img
                      src={icon}
                      alt={label}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        padding: '8px',
                        boxSizing: 'border-box',
                      }}
                    />
                  )}
                </div>
                {/* Upload button */}
                <div
                  onClick={() => fileInputRefs.current[key]?.click()}
                  style={{
                    height: '100px',
                    borderRadius: '8px',
                    border: '1px dashed #0048B3',
                    backgroundColor: '#EFF6FF',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    gap: '6px',
                  }}
                >
                  {uploading[key] ? (
                    <span style={{ fontSize: '12px', color: '#0048B3' }}>{t.common.uploading}</span>
                  ) : (
                    <>
                      <img
                        src="https://ts-upload.sgp1.digitaloceanspaces.com/cms/1XbbTYT49X9WL7P9DaKJiaNFJ83VTt8P4kzxBixSgoPwuPYaQe6Cgq73PCCNq8hhgjLTMF2jbTDd71hXQK6npkcS.png"
                        alt="upload"
                        style={{ width: '32px', height: '32px', objectFit: 'contain' }}
                      />
                      <span style={{ fontSize: '12px', color: '#0048B3', textAlign: 'center' }}>
                        {t.common.uploadOrTakePhoto}
                      </span>
                    </>
                  )}
                </div>
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png"
                  ref={(el) => {
                    fileInputRefs.current[key] = el
                  }}
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) handleUpload(key, file)
                    e.target.value = ''
                  }}
                />
              </div>
            </div>
          )
        })}
      </div>

      <BottomNav
        onBack={onBack}
        onNext={handleConfirm}
        nextLabel={t.common.confirm}
        nextDisabled={!hasAtLeastOneImage}
      />

      <CarWithDriverCarTypeModal
        open={showCarTypeModal}
        selectedType={carType}
        onSelect={handleCarTypeSelect}
        onClose={() => setShowCarTypeModal(false)}
      />

      <style>{modalStyles}</style>

      {/* Brand modal */}
      <Modal
        centered
        title={t.addCar.selectBrand}
        open={showBrandModal}
        onCancel={() => {
          setShowBrandModal(false)
          setBrandSearch('')
        }}
        footer={null}
        width={490}
        className="cwd-brand-modal"
        destroyOnClose
      >
        <div className="cwd-search-input" style={{ marginBottom: '16px' }}>
          <Input
            prefix={<SearchOutlined />}
            placeholder={t.addCar.searchBrand}
            value={brandSearch}
            onChange={(e) => setBrandSearch(e.target.value)}
            allowClear
          />
        </div>
        {filteredBrands.length === 0 ? (
          <div className="cwd-no-data">
            {brands.length === 0 ? t.addCar.noBrandData : t.addCar.noBrandFound}
          </div>
        ) : (
          <Row gutter={[8, 0]}>
            {filteredBrands.map((brand) => (
              <Col span={8} key={brand.key}>
                <div
                  className={`cwd-brand-grid-item ${carBrand === brand.key ? 'selected' : ''}`}
                  onClick={() => {
                    if (brand.key !== carBrand) {
                      setCarBrand(brand.key)
                      setModel('')
                      setCarType('')
                      setCarTypeAuto(false)
                    }
                    setErrors((prev) => ({ ...prev, carBrand: '' }))
                    setShowBrandModal(false)
                    setBrandSearch('')
                  }}
                >
                  {brand.icon_url && brand.icon_url !== 'undefined' && brand.icon_url !== '' ? (
                    <img
                      src={brand.icon_url}
                      alt={brandName(brand)}
                      className="cwd-brand-logo"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none'
                      }}
                    />
                  ) : (
                    <div
                      className="cwd-brand-logo"
                      style={{
                        backgroundColor: '#f5f5f5',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        color: '#999',
                        border: '1px solid #e0e0e0',
                        borderRadius: '4px',
                      }}
                    >
                      {brandName(brand).charAt(0)}
                    </div>
                  )}
                  <div className="cwd-brand-name">{brandName(brand)}</div>
                </div>
              </Col>
            ))}
          </Row>
        )}
      </Modal>

      {/* Model modal: model → sub-model */}
      <Modal
        centered
        title={
          modelStep === 'subModel' ? (
            <div className="cwd-model-modal-title">
              <span
                className="cwd-model-modal-back"
                onClick={() => {
                  setModelStep('model')
                  setActiveModelKey(null)
                }}
              >
                <ArrowLeftOutlined />
              </span>
              <span>
                {activeGroup?.label} — {t.addCar.selectSubModel}
              </span>
            </div>
          ) : (
            t.addCar.selectModel
          )
        }
        open={showModelModal}
        onCancel={closeModelModal}
        footer={null}
        width={490}
        className="cwd-model-modal"
        destroyOnClose
      >
        {modelStep === 'model' ? (
          <>
            <div className="cwd-search-input" style={{ marginBottom: '16px' }}>
              <Input
                prefix={<SearchOutlined />}
                placeholder={t.addCar.searchModel}
                value={modelSearch}
                onChange={(e) => setModelSearch(e.target.value)}
                allowClear
              />
            </div>
            {filteredModelGroups.length === 0 ? (
              <div className="cwd-no-data">
                {models.length === 0 ? t.addCar.noModelData : t.addCar.noModelFound}
              </div>
            ) : (
              <Row gutter={[8, 0]}>
                {filteredModelGroups.map((g) => (
                  <Col span={8} key={g.key}>
                    <div
                      className={`cwd-model-pill ${selectedModel?.key === g.key ? 'selected' : ''}`}
                      onClick={() => handleModelGroupSelect(g)}
                    >
                      <div className="cwd-model-pill-text">{g.label}</div>
                    </div>
                  </Col>
                ))}
              </Row>
            )}
          </>
        ) : (
          <Row gutter={[8, 0]}>
            {visibleSubModels.map((m) => (
              <Col span={8} key={`${m.key}-${m.sub_model}-${m.description_th}`}>
                <div
                  className={`cwd-model-pill ${model === m.description_th ? 'selected' : ''}`}
                  onClick={() => {
                    handleModelSelect(m)
                    closeModelModal()
                  }}
                >
                  <div className="cwd-model-pill-text">{subModelLabel(m)}</div>
                </div>
              </Col>
            ))}
          </Row>
        )}
      </Modal>
    </div>
  )
}

export default CarWithDriverAddCarForm
